/**
 * "What's new in NU Assist" — the landscape carousel at the bottom of the home
 * page (Jose, 2026-10-08, artifact v3 section 3; v8 on 2026-10-09: four
 * slides, 10 s, an endless forward loop, no pill, specific links). The
 * slides, their 30-day window and the link rules live in `whatsNew.ts`; this
 * file is the behaviour.
 *
 *   - Auto-play every 10 s, never under `prefers-reduced-motion`. Each move
 *     restarts the countdown, so a manual step is never followed by an
 *     instant auto step.
 *   - AN ENDLESS, FORWARD-ONLY LOOP. Every slide sits in the same grid cell,
 *     shifted one width left or right of the visible one, and after the last
 *     slide comes a copy of the first (`aria-hidden`, `inert`). "Next" from
 *     the last slide slides onto that copy, then jumps to the real first
 *     slide with no transition, so the carousel never rewinds across the
 *     others. "Previous" from the first slide does the reverse: it jumps to
 *     the copy, then slides back. Only the two slides of a move animate; any
 *     slide a dot skips over jumps unseen. Under reduced motion nothing
 *     animates and the copy is never visited.
 *   - A Pause / Play button, the first control (WCAG 2.2.2 Pause, Stop, Hide,
 *     Level A; the APG carousel's rotation control — added by the lead in the
 *     PR #1032 review). A paused carousel stays paused until Play: leaving
 *     with the pointer or the keyboard does not resume it. Under reduced
 *     motion it starts paused, and Play is the reader's explicit opt-in.
 *   - On top of that, a TEMPORARY pause while the pointer is over the
 *     carousel or keyboard focus is inside it. Focus is told apart by its
 *     ORIGIN, not by where the pointer rests: focus that a pointer press
 *     brings (a click on an arrow) does not count, so the carousel resumes
 *     once the pointer leaves; focus that arrives any other way (Tab) pauses
 *     it until focus leaves, even with the mouse parked over the carousel.
 *   - Previous / next arrows and one dot per slide (`aria-current` on the
 *     active one); ArrowLeft / ArrowRight step while focus is inside. Every
 *     slide but the visible one is `inert`, so an arrow key pressed on a
 *     slide's link moves focus to the NEW slide's link instead of letting it
 *     fall to <body> with the slide that left.
 *   - APG carousel roles: the region is labelled by the title, each slide is
 *     a `group` with `aria-roledescription="slide"`. The slides' wrapper is a
 *     polite live region only after a MANUAL move; auto-play sets it back to
 *     `off`, so a screen reader is not interrupted every 10 s.
 *
 * Links go to the most specific place first, from `GET
 * /api/web/v2/whats-new/links` (`useWhatsNewLinks`). The carousel renders at
 * once with the general fallbacks and swaps the specific hrefs in when the
 * query answers; on an error it keeps the fallbacks. A slide whose target the
 * reader cannot open (the left menu's `pageVisible` gates) is not shown at
 * all, as the menu's "new" pills are not.
 *
 * Design-system token utilities only; no inline `style`; English copy only.
 */
import type { WhatsNewLinks } from "@nu-assist/contracts";
import { useMe } from "@nu-assist/shared-fe";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import {
  type FocusEvent,
  type KeyboardEvent,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";

import { type WhatsNewClient, createWhatsNewClient } from "../../clients/whatsNew.client";
import { usePrefersReducedMotion } from "../../features/imports/CleanupCelebration";
import { useFeatureAccess } from "../../features/useFeatureAccess";
import { SEARCHES_HREF } from "./MyOpenSearchesWidget";
import { WhatsNewDrawing } from "./WhatsNewIllustrations";
import {
  WHATS_NEW_SLIDES,
  type WhatsNewSlide,
  activeWhatsNewSlides,
  resolveSlideLinks,
} from "./whatsNew";

/** How long each slide stays before auto-play moves on. */
export const WHATS_NEW_AUTOPLAY_MS = 10_000;

/**
 * How long one slide takes to slide in. Must match the `duration-500` class
 * on the slides: the jump off the copy of the first slide waits for it.
 */
export const WHATS_NEW_SLIDE_MS = 500;

/** How long the links answer is reused before it is asked again. */
export const WHATS_NEW_LINKS_STALE_MS = 5 * 60_000;

export const WHATS_NEW_TITLE = "What’s new in NU Assist";

const API_BASE = import.meta.env.VITE_API_URL ?? "https://api.local.nu-assist.com";
const defaultClient = createWhatsNewClient({ apiBase: API_BASE });

/**
 * The caller's most specific link targets, asked once and reused for
 * `WHATS_NEW_LINKS_STALE_MS`. Enabled only when a shown slide needs it.
 *
 * Keyed on the signed-in email (`useMe`), because the answer is the caller's:
 * logout is an in-app navigation, not a reload, so a second user signing in
 * on the same tab would otherwise get the first one's links for up to the
 * stale time. Off until the session has resolved.
 */
export function useWhatsNewLinks(client: WhatsNewClient = defaultClient, enabled = true) {
  const me = useMe();
  const email = me.status === "authenticated" ? me.data.email : null;
  return useQuery<WhatsNewLinks>({
    queryKey: ["whats-new", "links", email],
    queryFn: () => client.getLinks(),
    staleTime: WHATS_NEW_LINKS_STALE_MS,
    enabled: enabled && email !== null,
  });
}

export interface WhatsNewCarouselProps {
  /** `YYYY-MM-DD`; injectable for tests. Defaults to today (UTC). */
  today?: string;
  /** Injectable for tests (defaults to the module-shared client). */
  client?: WhatsNewClient;
  /** Injectable for tests; defaults to `WHATS_NEW_SLIDES`. */
  slides?: readonly WhatsNewSlide[];
  className?: string;
  /**
   * Picks the slide the carousel opens on, once per mount: a returning reader
   * sees a different feature first (Jose, 2026-10-20). Returns `[0, 1)`, like
   * `Math.random` (the default); tests inject a fixed one.
   */
  random?: () => number;
}

interface ShownSlide {
  slide: WhatsNewSlide;
  href: string;
  /** The second link's href, for a slide that has one. */
  secondaryHref: string | null;
}

export function WhatsNewCarousel({
  today,
  client,
  slides = WHATS_NEW_SLIDES,
  className,
  random = Math.random,
}: WhatsNewCarouselProps) {
  const access = useFeatureAccess();
  const isVisible = (sectionId: string, pageId: string) => access.page(sectionId, pageId).visible;
  // In the window AND openable by this reader; the endpoint's answer only
  // refines the href, it never decides whether a slide shows.
  const inWindow = activeWhatsNewSlides(slides, today).filter(
    (s) => resolveSlideLinks(s, isVisible, undefined, SEARCHES_HREF) !== null
  );
  const links = useWhatsNewLinks(
    client,
    inWindow.some((s) => s.link.kind === "search-tab" || s.link.kind === "reviewed-person")
  );
  const answer = links.isError ? undefined : links.data;

  const shown: ShownSlide[] = inWindow.map((slide) => {
    const resolved = resolveSlideLinks(slide, isVisible, answer, SEARCHES_HREF);
    return {
      slide,
      href: resolved?.href ?? SEARCHES_HREF,
      secondaryHref: resolved?.secondaryHref ?? null,
    };
  });
  if (shown.length === 0) return null;
  return <Carousel shown={shown} className={className} random={random} />;
}

/**
 * Where the track stands. `pos` runs from 0 to `count`, where `count` is the
 * copy of the first slide. `from` is the slot that was visible before the
 * last move: it and `pos` are the only slots that animate, and only when
 * `animate`. `settleTo`, when set, is a second move to make right after this one
 * is laid out: the slide back off the copy that "Previous" from the first
 * slide starts with, or the step after a press that cut the slide onto the
 * copy short.
 */
interface Track {
  pos: number;
  from: number;
  animate: boolean;
  settleTo?: number;
}

const AT_START: Track = { pos: 0, from: 0, animate: false };

/**
 * Where the track opens: at rest on a random slide of `count`, with no
 * animation. `random` returns `[0, 1)`; anything outside that is clamped, so a
 * bad injection still lands on a real slide.
 */
export function startingTrack(count: number, random: () => number): Track {
  if (count < 2) return AT_START;
  const pos = Math.min(count - 1, Math.max(0, Math.floor(random() * count)));
  return { pos, from: pos, animate: false };
}

/** Sliding onto the copy of the first slide, before the jump to the real one. */
function onTheWayToTheCopy(t: Track, count: number): boolean {
  return count > 1 && t.pos === count && t.animate;
}

/**
 * The track as it may stand for `count` slides. The copy is never a resting
 * place: only the slide onto it may sit there, until the jump, and the jump
 * onto it that "Previous" lays out, until the slide back. A slide list
 * that shrank under the track (a gate closed after mount) leaves `pos` on the
 * copy or past it; it starts over at the first slide.
 */
export function restingTrack(t: Track, count: number): Track {
  if (count < 2) return t.pos === 0 && !t.animate ? t : AT_START;
  if (t.pos < count || onTheWayToTheCopy(t, count)) return t;
  // The jump to the copy that "Previous" from the first slide lays out
  // before sliding back (`settleTo`): transient, and it must render on the
  // copy, or the slide back starts from the first slide and runs the wrong way.
  if (t.pos === count && t.settleTo !== undefined) return t;
  return AT_START;
}

/*
 * A press during the slide onto the copy (WHATS_NEW_SLIDE_MS) is not dropped.
 * "Next" and a dot jump to the real first slide at once and move on from
 * there (`settleTo`); "Previous" slides back from the copy to the last slide,
 * which is an ordinary step.
 */

function stepForward(t: Track, count: number, reduced: boolean): Track {
  if (onTheWayToTheCopy(t, count)) return { pos: 0, from: count, animate: false, settleTo: 1 };
  if (t.pos === count - 1) {
    return reduced
      ? { pos: 0, from: t.pos, animate: false }
      : { pos: count, from: t.pos, animate: true };
  }
  return { pos: t.pos + 1, from: t.pos, animate: !reduced };
}

function stepBack(t: Track, count: number, reduced: boolean): Track {
  if (t.pos === 0) {
    return reduced
      ? { pos: count - 1, from: 0, animate: false }
      : { pos: count, from: 0, animate: false, settleTo: count - 1 };
  }
  return { pos: t.pos - 1, from: t.pos, animate: !reduced };
}

function stepTo(t: Track, i: number, count: number, reduced: boolean): Track {
  if (onTheWayToTheCopy(t, count)) {
    const landed = { pos: 0, from: count, animate: false };
    return i === 0 ? landed : { ...landed, settleTo: i };
  }
  return t.pos === i ? t : { pos: i, from: t.pos, animate: !reduced };
}

function Carousel({
  shown,
  className,
  random,
}: {
  shown: ShownSlide[];
  className?: string;
  random: () => number;
}) {
  const titleId = useId();
  // A random first slide, chosen once per mount (the lazy initializer).
  const [track, setTrack] = useState<Track>(() => startingTrack(shown.length, random));
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [live, setLive] = useState<"off" | "polite">("off");
  const reducedMotion = usePrefersReducedMotion();
  // The reader's Pause / Play choice; until they make one, reduced motion
  // decides (paused under it, playing otherwise).
  const [userPaused, setUserPaused] = useState<boolean | null>(null);
  const paused = userPaused ?? reducedMotion;
  const trackRef = useRef<HTMLDivElement>(null);
  const linkRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const secondaryLinkRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  // Set when an arrow key moves the carousel from a slide's link: which of
  // its links had focus, so focus goes to the same one on the new slide.
  const followWithFocus = useRef<"primary" | "secondary" | null>(null);

  const count = shown.length;
  const view = restingTrack(track, count);
  const pos = view.pos;
  const current = pos % count;
  const playing = count > 1 && !paused && !hovered && !focused;

  const next = () => setTrack((t) => stepForward(restingTrack(t, count), count, reducedMotion));
  const prev = () => setTrack((t) => stepBack(restingTrack(t, count), count, reducedMotion));

  // Store the resting track when the slide list changes size, so no later
  // step starts from a slot that no longer exists.
  useEffect(() => {
    setTrack((t) => restingTrack(t, count));
  }, [count]);

  // Keyed on `current`, so every move (auto or manual) restarts the 10 s.
  // The jump off the copy keeps `current` at 0 and does not restart it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `current` is the restart key, not a value the effect reads.
  useEffect(() => {
    if (!playing) return;
    const timer = setTimeout(() => {
      setLive("off");
      setTrack((t) => stepForward(restingTrack(t, count), count, reducedMotion));
    }, WHATS_NEW_AUTOPLAY_MS);
    return () => clearTimeout(timer);
  }, [playing, current, count, reducedMotion]);

  // Once the copy of the first slide has slid in, swap the real one in under
  // it, unseen.
  useEffect(() => {
    if (!onTheWayToTheCopy(track, count)) return;
    const timer = setTimeout(
      () => setTrack({ pos: 0, from: count, animate: false }),
      WHATS_NEW_SLIDE_MS
    );
    return () => clearTimeout(timer);
  }, [track, count]);

  // A jump that a slide follows ("Previous" from the first slide, or a press
  // during the slide onto the copy) is laid out first (reading `offsetWidth`
  // makes the browser compute it), so the slide starts from there.
  useLayoutEffect(() => {
    if (track.settleTo === undefined) return;
    void trackRef.current?.offsetWidth;
    setTrack({ pos: track.settleTo, from: track.pos, animate: true });
  }, [track]);

  useEffect(() => {
    const from = followWithFocus.current;
    if (from === null) return;
    followWithFocus.current = null;
    // The new slide's second link when focus left from a second link and the
    // new slide has one; its first link otherwise.
    const target =
      (from === "secondary" ? secondaryLinkRefs.current[current] : null) ??
      linkRefs.current[current];
    target?.focus({ preventScroll: true });
  }, [current]);

  const goTo = (i: number) => {
    setLive("polite");
    setTrack((t) => stepTo(restingTrack(t, count), i, count, reducedMotion));
  };
  const goNext = () => {
    setLive("polite");
    next();
  };
  const goPrev = () => {
    setLive("polite");
    prev();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (count < 2 || (e.key !== "ArrowLeft" && e.key !== "ArrowRight")) return;
    e.preventDefault();
    const target = e.target as HTMLAnchorElement;
    followWithFocus.current = linkRefs.current.includes(target)
      ? "primary"
      : secondaryLinkRefs.current.includes(target)
        ? "secondary"
        : null;
    if (e.key === "ArrowLeft") goPrev();
    else goNext();
  };

  // A pointer press focuses the button it lands on, between `pointerdown`
  // and `pointerup`; that focus is the pointer's, and the hover pause already
  // covers it. Any other focus (Tab, a script) is the keyboard's.
  const pointerPressing = useRef(false);
  const onFocus = () => {
    if (!pointerPressing.current) setFocused(true);
  };
  const endPress = () => {
    pointerPressing.current = false;
  };

  const onBlur = (e: FocusEvent<HTMLElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
  };

  // The slots: every slide, then the copy of the first one.
  const slots = count > 1 ? [...shown, shown[0] as ShownSlide] : shown;
  const placement = (slot: number) => {
    const offset =
      slot < pos ? "-translate-x-full" : slot > pos ? "translate-x-full" : "translate-x-0";
    const moves = view.animate && !reducedMotion && (slot === pos || slot === view.from);
    return `${offset} ${moves ? "transition-transform duration-500 ease-in-out motion-reduce:transition-none" : "transition-none"}`;
  };

  return (
    <section
      data-testid="whats-new"
      aria-roledescription="carousel"
      aria-labelledby={titleId}
      data-playing={playing ? "true" : "false"}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => {
        setHovered(false);
        endPress();
      }}
      onPointerDown={() => {
        pointerPressing.current = true;
      }}
      onPointerUp={endPress}
      onPointerCancel={endPress}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      className={["flex w-full flex-col rounded-lg border border-border bg-surface", className]
        .filter(Boolean)
        .join(" ")}
    >
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-5 py-4">
        <div className="flex flex-col gap-0.5">
          <h2 id={titleId} className="text-h2 text-text">
            {WHATS_NEW_TITLE}
          </h2>
          <p className="text-caption text-text-secondary">
            The latest features, released in the last 30 days
          </p>
        </div>
        {count > 1 ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label={paused ? "Play slides" : "Pause slides"}
              data-testid="whats-new-rotation"
              onClick={() => setUserPaused(!paused)}
              className="rounded-md p-1 text-text-muted hover:bg-surface-raised hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {paused ? (
                <Play className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Pause className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
            <button
              type="button"
              aria-label="Previous slide"
              data-testid="whats-new-prev"
              onClick={goPrev}
              className="rounded-md p-1 text-text-muted hover:bg-surface-raised hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            {shown.map(({ slide: s }, i) => (
              <button
                key={s.id}
                type="button"
                aria-label={`Show slide ${i + 1} of ${count}: ${s.title}`}
                aria-current={i === current ? "true" : undefined}
                data-testid="whats-new-dot"
                onClick={() => goTo(i)}
                className="rounded-full p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                <span
                  className={`block h-2 w-2 rounded-full ${i === current ? "bg-accent" : "bg-border"}`}
                />
              </button>
            ))}
            <button
              type="button"
              aria-label="Next slide"
              data-testid="whats-new-next"
              onClick={goNext}
              className="rounded-md p-1 text-text-muted hover:bg-surface-raised hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </header>

      <div aria-live={live} data-testid="whats-new-slides" className="overflow-hidden rounded-b-lg">
        <div
          ref={trackRef}
          data-testid="whats-new-track"
          data-pos={pos}
          data-animate={view.animate && !reducedMotion ? "true" : "false"}
          className="grid"
        >
          {slots.map(({ slide, href, secondaryHref }, slot) => {
            const copy = slot === count;
            const visible = slot === current && !copy;
            return (
              <div
                key={copy ? "copy-of-first" : slide.id}
                role={copy ? undefined : "group"}
                aria-roledescription={copy ? undefined : "slide"}
                aria-label={copy ? undefined : `${slot + 1} of ${count}`}
                aria-hidden={visible ? undefined : "true"}
                inert={!visible}
                data-testid={copy ? "whats-new-copy" : "whats-new-slide"}
                data-slide-id={slide.id}
                data-current={visible ? "true" : undefined}
                className={`col-start-1 row-start-1 grid grid-cols-1 sm:grid-cols-12 ${placement(slot)}`}
              >
                <div className="flex min-h-56 items-center justify-center border-b border-border bg-surface-raised p-4 sm:col-span-5 sm:border-b-0 sm:border-r">
                  <WhatsNewDrawing illustration={slide.illustration} />
                </div>
                {/* The artwork sits ABOVE the text, small, until `xl`, and
                    beside it only from `xl` (1280 px) up: at 1024 px a 144 px picture
                    beside the 7/12 column left the text about 192 px. */}
                <div className="flex min-w-0 flex-col items-start justify-center gap-3 p-5 sm:col-span-7 xl:flex-row xl:items-center xl:gap-4">
                  {slide.artwork ? (
                    // Decorative, as on the ChopBook page: the title names it.
                    // The PNG is opaque (white ground), so no background class.
                    <img
                      src={slide.artwork.src}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      data-testid="whats-new-artwork"
                      className="h-16 w-16 shrink-0 rounded-lg object-contain shadow-md xl:h-36 xl:w-36"
                    />
                  ) : null}
                  <div className="flex min-w-0 flex-col items-start gap-2">
                    <h3 className="text-h3 font-semibold text-text">{slide.title}</h3>
                    <p className="text-body text-text-secondary">
                      {slide.lead ? (
                        <strong className="font-semibold text-text">{slide.lead}</strong>
                      ) : null}
                      {slide.body}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                      <Link
                        to={href}
                        ref={(node) => {
                          if (!copy) linkRefs.current[slot] = node;
                        }}
                        data-testid={copy ? undefined : "whats-new-link"}
                        className="text-body font-medium text-accent-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
                      >
                        {slide.link.label} →
                      </Link>
                      {slide.secondaryLink && secondaryHref !== null ? (
                        <Link
                          to={secondaryHref}
                          ref={(node) => {
                            if (!copy) secondaryLinkRefs.current[slot] = node;
                          }}
                          data-testid={copy ? undefined : "whats-new-link-secondary"}
                          className="text-body font-medium text-accent-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
                        >
                          {slide.secondaryLink.label} →
                        </Link>
                      ) : null}
                    </div>
                    {slide.note ? (
                      <p className="text-caption text-text-muted">{slide.note}</p>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
